import React, { useState, useContext } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import AppContext from '../../contexts/AppContext';
import { signInWithGoogle, resetPassword } from '../../services/authService';
import { verifyRecaptcha } from '../../services/firestoreService';
import { getRecaptchaToken } from '../../utils/recaptcha';
import GoogleIcon from '../common/GoogleIcon';
import RegistrationScreen from './RegistrationScreen';

const LoginScreen = () => {
  const { login, showAlert, effectiveTheme } = useContext(AppContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMsg, setResetMsg] = useState(null);

  const handleLogin = async () => {
    setLoginLoading(true);
    try {
      await login(email, password);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Auth state listener will handle the rest (either show main app or profile completion)
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        await showAlert('Google sign-in failed: ' + err.message, 'Error');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  if (showRegistration) {
    return <RegistrationScreen onBackToLogin={() => setShowRegistration(false)} />;
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-gray-900 to-black overflow-y-auto">
      <div className="min-h-full flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-black rounded-lg shadow-xl border border-gray-600 p-8 my-4">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img src="/logos/logo-transparent-smll.png" alt="" className="h-12 w-auto object-contain" />
            <img
              src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
              alt="Loft"
              className="h-12 w-auto object-contain"
            />
          </div>
          <p className="text-white mt-2">Connect. Play. Grow.</p>
          <ul className="text-sm text-gray-300 mt-4 leading-relaxed text-left space-y-1.5">
            <li>• Share photos, videos, and updates on the social feed</li>
            <li>• Follow golfers and build your network</li>
            <li>• Message friends directly or in group chats</li>
            <li>• Create and join golf events with RSVP</li>
            <li>• Score your rounds hole by hole in real time</li>
            <li>• Choose from 12 game types including Stroke, Nassau, and Skins</li>
            <li>• Pause and resume scorecards anytime</li>
            <li>• Review your full scorecard history</li>
            <li>• Discover golfers near you with search and filters</li>
            <li>• Buy and sell gear on the marketplace</li>
            <li>• Find golf courses near you</li>
            <li>• Search over 15,000 courses across the US</li>
            <li>• Auto-calculate your handicap index after 10 rounds</li>
            <li className="pt-2 pb-0.5 text-yellow-500 font-semibold">Caddie Pro</li>
            <li>• Get AI coaching tips based on your game</li>
            <li>• Get personalized AI course recommendations</li>
            <li>• Climb the leaderboards and track your progress</li>
          </ul>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-white mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-2 py-1 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
              placeholder="your@email.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                className="w-full px-2 py-1 pr-10 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#22c55e] text-white bg-black"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <button onClick={handleLogin} disabled={loginLoading} className="w-full bg-[#16a34a] text-pure-white py-2 rounded-lg font-medium hover:bg-[#15803d] disabled:opacity-50">
            {loginLoading ? 'Signing In...' : 'Sign In'}
          </button>

          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-gray-700" />
            <span className="px-3 text-sm text-gray-400">or</span>
            <div className="flex-1 border-t border-gray-700" />
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading}
            className="w-full bg-white text-gray-800 py-2 rounded-lg font-medium hover:bg-gray-100 disabled:opacity-50 flex items-center justify-center space-x-2"
          >
            <GoogleIcon />
            <span>{googleLoading ? 'Signing In...' : 'Sign in with Google'}</span>
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-white">
            Don't have an account?{' '}
            <button
              onClick={() => setShowRegistration(true)}
              className="text-[#16a34a] hover:text-[#15803d] font-medium"
            >
              Sign Up
            </button>
          </p>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-800">
          {!showForgotPassword ? (
            <p className="text-sm text-white text-center">
              <button onClick={() => { setShowForgotPassword(true); setResetMsg(null); setResetEmail(email); }} className="text-[#16a34a] hover:text-[#15803d] font-medium">Forgot Password?</button>
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-white text-center font-medium">Reset your password</p>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-2 py-1 border border-gray-700 rounded-lg text-white bg-black text-sm"
              />
              {resetMsg && (
                <p className={`text-sm text-center ${resetMsg.type === 'error' ? 'text-red-500' : 'text-green-500'}`}>{resetMsg.text}</p>
              )}
              <div className="flex space-x-2">
                <button
                  onClick={async () => {
                    if (!resetEmail.trim()) { setResetMsg({ type: 'error', text: 'Please enter your email' }); return; }
                    try {
                      const token = await getRecaptchaToken('password_reset');
                      await verifyRecaptcha(token, 'password_reset');
                      await resetPassword(resetEmail.trim());
                      setResetMsg({ type: 'success', text: 'Reset link sent! Check your email.' });
                    } catch (err) {
                      console.error('resetPassword error:', err?.code, err?.message);
                      if (err.code === 'functions/permission-denied') {
                        setResetMsg({ type: 'error', text: 'Your request was flagged as suspicious. Please try again.' });
                      } else {
                        setResetMsg({ type: 'error', text: 'Failed to send reset email. Check the email and try again.' });
                      }
                    }
                  }}
                  className="flex-1 bg-[#16a34a] text-pure-white py-1 rounded-lg font-medium hover:bg-[#15803d] text-sm"
                >
                  Send Reset Link
                </button>
                <button
                  onClick={() => { setShowForgotPassword(false); setResetMsg(null); }}
                  className="px-3 py-1 text-white hover:bg-gray-800 rounded-lg text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      <div className="text-center pb-6 text-xs text-gray-500 space-x-3">
        <a
          href="/terms"
          onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/terms'); window.dispatchEvent(new PopStateEvent('popstate')); }}
          className="hover:text-gray-300 underline"
        >
          Terms of Use
        </a>
        <span>·</span>
        <a
          href="/privacypolicy"
          onClick={e => { e.preventDefault(); window.history.pushState({}, '', '/privacypolicy'); window.dispatchEvent(new PopStateEvent('popstate')); }}
          className="hover:text-gray-300 underline"
        >
          Privacy Policy
        </a>
      </div>
    </div>
  );
};

export default LoginScreen;
